export const LAMPORTS_PER_SOL = 1_000_000_000;

export function lamportsToSol(lamports: number): number {
  return lamports / LAMPORTS_PER_SOL;
}

export function solToLamports(sol: number): number {
  return Math.floor(sol * LAMPORTS_PER_SOL);
}

export function formatSol(lamports: number, decimals = 4): string {
  const sol = lamportsToSol(lamports);
  if (sol === 0) return '0 SOL';
  if (sol < 0.001) return `${sol.toFixed(6)} SOL`;
  return `${sol.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: decimals })} SOL`;
}

export function formatSolValue(lamports: number): string {
  return lamportsToSol(lamports).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

export interface Countdown {
  total: number; // seconds remaining
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

export function getCountdown(unlockAtSeconds: number): Countdown {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const total = unlockAtSeconds - nowSeconds;
  if (total <= 0) {
    return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { total, days, hours, minutes, seconds, expired: false };
}

export function formatDuration(days: number): string {
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''}`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    const rem = days % 7;
    if (rem === 0) return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    return `${weeks}w ${rem}d`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    const rem = days % 30;
    if (rem === 0) return `${months} month${months !== 1 ? 's' : ''}`;
    return `${months}mo ${rem}d`;
  }
  const years = Math.floor(days / 365);
  const rem = days % 365;
  const months = Math.floor(rem / 30);
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`;
  return `${years}yr ${months}mo`;
}

export function formatCountdown(cd: Countdown): string {
  if (cd.expired) return 'Unlockable';
  const parts: string[] = [];
  if (cd.days > 0) parts.push(`${cd.days}d`);
  parts.push(`${String(cd.hours).padStart(2, '0')}h`);
  parts.push(`${String(cd.minutes).padStart(2, '0')}m`);
  parts.push(`${String(cd.seconds).padStart(2, '0')}s`);
  return parts.join(' ');
}

export function getProgress(tsSeconds: number, unlockAtSeconds: number): number {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const total = unlockAtSeconds - tsSeconds;
  if (total <= 0) return 100;
  const elapsed = nowSeconds - tsSeconds;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

export function formatUnlockDate(unlockAtSeconds: number): string {
  return new Date(unlockAtSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function getCommitmentLevel(days: number): { label: string; color: string } {
  if (days <= 7) return { label: 'Starter', color: 'hsl(35 8% 52%)' };
  if (days <= 30) return { label: 'Committed', color: 'hsl(38 65% 57%)' };
  if (days <= 90) return { label: 'Disciplined', color: 'hsl(43 75% 68%)' };
  if (days <= 365) return { label: 'Steadfast', color: 'hsl(50 80% 72%)' };
  return { label: 'Legendary', color: 'hsl(38 80% 65%)' };
}
